/**
 * Slice 56 — keep the whole job working with no signal.
 *
 * Every save the cleaner's job steps make (answers, counts, reports) and every photo upload goes through
 * `offlineAwareFetch`. With signal it behaves exactly as before. With no signal, or a signal too weak to
 * finish the request, the request is saved on the phone and answered as if it worked, so the step moves on.
 * The saved requests are sent later, oldest first, answers before photos, retrying quietly. A saved photo
 * is shown straight from the phone (the service worker serves it at its real address) until it has sent.
 */

import { useMemo, useSyncExternalStore } from "react";
import { idbDelete, idbGet, idbGetAll, idbPut } from "@/lib/offline/idb";

export type OutboxStatus = "queued" | "failed";

export interface OutboxItem {
  id: string;
  kind: "write" | "photo";
  seq: number;
  createdAt: string;
  method: string;
  url: string;
  headers: Record<string, string>;
  /** JSON text for writes. */
  body?: string;
  /** Photos: where it goes. The picture itself is kept in the photos store under `path`. */
  photo?: { bucket: string; path: string; contentType: string; cacheControl: string; upsert: boolean };
  /** cleaning_tasks writes: which job, so the screens can show the saved answers meanwhile. */
  taskId?: string;
  /** Marks the small "photos still to come" count write for this job (the owner sees it while photos wait). */
  countFor?: string;
  status: OutboxStatus;
  attempts: number;
  nextAt: number;
  failReason?: string;
}

export interface StoredPhoto {
  /** `${bucket}/${path}` — the same text the service worker finds in the public URL. */
  path: string;
  blob: Blob;
  contentType: string;
}

/** Tables the job steps write to; anything else is never held back. */
const WRITE_TABLES = new Set([
  "cleaning_tasks",
  "maintenance_issue_reports",
  "cleaning_submissions",
  "inventory_submissions",
  "inventory_items",
  "cleaning_progress_temp",
  "task_submission_photos",
  "standard_proof_uploads",
  "unit_photo_asks",
]);
const RPC_NAMES = new Set(["mark_guest_left_on_cleaning_start", "snapshot_cleaning_task_fee_from_agreement"]);
const PHOTO_BUCKETS = new Set(["cleaning-photos"]);
/** New rows get their id here so a re-send can never make a second copy. */
const NEEDS_ID = new Set(["maintenance_issue_reports", "cleaning_submissions", "inventory_submissions", "task_submission_photos"]);

const REQUEST_TIMEOUT_MS = 20_000;
const PHOTO_TIMEOUT_MS = 60_000;
const BACKOFF_MIN_MS = 5_000;
const BACKOFF_MAX_MS = 5 * 60_000;

const realFetch: typeof fetch = (input, init) => globalThis.fetch(input, init);

// ---------- in-memory mirror of the saved requests ----------

let items: OutboxItem[] = [];
let seqCounter = 0;
let version = 0;
let loaded = false;
const listeners = new Set<() => void>();
const drainedListeners = new Set<() => void>();
/** Items being sent right now (the "sending" state in the screens). */
const uploading = new Set<string>();
/** Photos that already got through this visit, per job, so "3 of 8 sent" can be shown. */
const sentPhotos = new Map<string, { before: number; after: number; other: number }>();

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

export function onOutboxDrained(cb: () => void): () => void {
  drainedListeners.add(cb);
  return () => {
    drainedListeners.delete(cb);
  };
}

export async function loadOutbox(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const stored = await idbGetAll<OutboxItem>("outbox");
    const byId = new Map(items.map((i) => [i.id, i]));
    for (const it of stored) if (!byId.has(it.id)) items.push(it);
    items.sort((a, b) => a.seq - b.seq);
    seqCounter = items.reduce((m, i) => Math.max(m, i.seq), seqCounter);
    emit();
  } catch {
    /* no storage: the outbox just works for this visit */
  }
}

async function saveItem(item: OutboxItem) {
  try {
    await idbPut("outbox", item);
  } catch {
    /* kept in memory */
  }
}

async function removeItem(item: OutboxItem) {
  items = items.filter((i) => i.id !== item.id);
  try {
    await idbDelete("outbox", item.id);
    if (item.kind === "photo" && item.photo) await idbDelete("photos", `${item.photo.bucket}/${item.photo.path}`);
  } catch {
    /* ignore */
  }
  emit();
  if (items.length === 0) drainedListeners.forEach((cb) => cb());
}

// ---------- store for the screens ----------

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useOutboxVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => 0);
}

export function getOutboxItems(): OutboxItem[] {
  return items;
}

export function getOutboxCounts(): { writes: number; photos: number; failed: number; total: number } {
  const writes = items.filter((i) => i.kind === "write" && i.status === "queued").length;
  const photos = items.filter((i) => i.kind === "photo" && i.status === "queued").length;
  const failed = items.filter((i) => i.status === "failed").length;
  return { writes, photos, failed, total: items.length };
}

export function useOutboxCounts() {
  const v = useOutboxVersion();
  return useMemo(() => getOutboxCounts(), [v]);
}

/** The saved answers for one job, merged oldest to newest, so the job screens show what the cleaner just did. */
export function getPendingTaskFields(taskId: string): Record<string, unknown> | null {
  const mine = items.filter((i) => i.kind === "write" && i.taskId === taskId && i.method === "PATCH" && i.body);
  if (mine.length === 0) return null;
  const out: Record<string, unknown> = {};
  for (const it of mine) {
    try {
      Object.assign(out, JSON.parse(it.body as string));
    } catch {
      /* skip */
    }
  }
  return out;
}

/** Job id inside a photo's storage path: `${propertyId}/${taskId}/...`. */
export function taskIdOfPhotoPath(path: string): string | null {
  const seg = path.split("/")[1];
  return seg && /^[0-9a-f-]{32,36}$/i.test(seg) ? seg : null;
}

/** What is still waiting on the phone for one job. */
export function getWaitingForTask(taskId: string): { photos: number; writes: number; failed: number } {
  let photos = 0;
  let writes = 0;
  let failed = 0;
  for (const it of items) {
    const mine = it.kind === "photo" ? it.photo && taskIdOfPhotoPath(it.photo.path) === taskId : it.taskId === taskId && !it.countFor;
    if (!mine) continue;
    if (it.status === "failed") failed += 1;
    else if (it.kind === "photo") photos += 1;
    else writes += 1;
  }
  return { photos, writes, failed };
}

export type QueueState = "waiting" | "sending" | "retrying";
export type PhotoGroup = "before" | "after" | "other";

export function photoGroupOf(path: string): PhotoGroup {
  return path.includes("/simple-before/") ? "before" : path.includes("/simple-after/") || path.includes("/after") ? "after" : "other";
}

function stateOf(it: OutboxItem): QueueState {
  return uploading.has(it.id) ? "sending" : it.attempts > 0 || it.status === "failed" ? "retrying" : "waiting";
}

export interface JobQueuePhoto {
  id: string;
  bucket: string;
  path: string;
  group: PhotoGroup;
  state: QueueState;
}
export interface JobQueueWrite {
  id: string;
  kind: "checklist" | "flag";
  state: QueueState;
}

/** Everything still on the phone for one job, plus how many already sent this visit, for the per-job view. */
export function getJobQueue(taskId: string) {
  const photos: JobQueuePhoto[] = [];
  const writes: JobQueueWrite[] = [];
  for (const it of items) {
    if (it.kind === "photo" && it.photo) {
      if (taskIdOfPhotoPath(it.photo.path) !== taskId) continue;
      photos.push({ id: it.id, bucket: it.photo.bucket, path: it.photo.path, group: photoGroupOf(it.photo.path), state: stateOf(it) });
    } else if (it.kind === "write" && it.taskId === taskId && !it.countFor) {
      writes.push({ id: it.id, kind: "checklist", state: stateOf(it) });
    } else if (it.kind === "write" && !it.taskId && !it.countFor) {
      writes.push({ id: it.id, kind: /maintenance_issue_reports/.test(it.url) ? "flag" : "checklist", state: stateOf(it) });
    }
  }
  const sent = sentPhotos.get(taskId) ?? { before: 0, after: 0, other: 0 };
  return { photos, writes, sent };
}

/** True if any send has already tried and not got through (a weak signal). */
export function hasStalledItems(): boolean {
  return items.some((i) => i.status === "queued" && i.attempts > 0);
}

// ---------- the fetch the Supabase client uses ----------

function describe(input: RequestInfo | URL, init?: RequestInit) {
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const method = (init?.method ?? (typeof input === "object" && "method" in input ? input.method : "GET") ?? "GET").toUpperCase();
  return { url, method };
}

function headersToRecord(h: HeadersInit | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!h) return out;
  const headers = new Headers(h);
  headers.forEach((v, k) => {
    out[k.toLowerCase()] = v;
  });
  return out;
}

type Target =
  | { kind: "write"; table: string; taskId?: string }
  | { kind: "rpc"; name: string }
  | { kind: "photo"; bucket: string; path: string };

function targetOf(url: string, method: string): Target | null {
  if (method !== "POST" && method !== "PATCH" && method !== "PUT") return null;
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  const rest = u.pathname.match(/\/rest\/v1\/([^/]+)$/);
  if (rest) {
    const name = rest[1];
    if (WRITE_TABLES.has(name)) {
      const idParam = u.searchParams.get("id");
      const taskId = name === "cleaning_tasks" && idParam?.startsWith("eq.") ? idParam.slice(3) : undefined;
      return { kind: "write", table: name, taskId };
    }
    return null;
  }
  const rpc = u.pathname.match(/\/rest\/v1\/rpc\/([^/]+)$/);
  if (rpc && RPC_NAMES.has(rpc[1])) return { kind: "rpc", name: rpc[1] };
  const obj = u.pathname.match(/\/storage\/v1\/object\/([^/]+)\/(.+)$/);
  if (obj && PHOTO_BUCKETS.has(obj[1]) && (method === "POST" || method === "PUT")) {
    return { kind: "photo", bucket: obj[1], path: decodeURIComponent(obj[2]) };
  }
  return null;
}

function isNetworkFailure(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  const msg = err instanceof Error ? err.message : "";
  return err instanceof TypeError || /failed to fetch|network|load failed|fetch failed|offline/i.test(msg);
}

function jsonResponse(status: number, body: unknown, extra?: Record<string, string>) {
  return new Response(body === undefined ? null : JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json", ...(extra ?? {}) },
  });
}

function newId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit | undefined, ms: number) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await realFetch(input, { ...(init ?? {}), signal: init?.signal ?? controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function waitingPhotoCount(taskId: string): number {
  return items.filter((i) => i.kind === "photo" && i.status === "queued" && i.photo && taskIdOfPhotoPath(i.photo.path) === taskId).length;
}

/** One small write per job, "N photos still to come", so the owner's report can say so. It goes before the photos. */
async function queueCountWrite(taskId: string, anyUrl: string, headers: Record<string, string>) {
  const stale = items.filter((i) => i.countFor === taskId && i.status === "queued");
  for (const old of stale) {
    items = items.filter((i) => i.id !== old.id);
    try {
      await idbDelete("outbox", old.id);
    } catch {
      /* ignore */
    }
  }
  let origin = "";
  try {
    origin = new URL(anyUrl).origin;
  } catch {
    return;
  }
  const item: OutboxItem = {
    id: newId(),
    kind: "write",
    seq: ++seqCounter,
    createdAt: new Date().toISOString(),
    method: "PATCH",
    url: `${origin}/rest/v1/cleaning_tasks?id=eq.${taskId}`,
    headers: { apikey: headers.apikey ?? "", "content-type": "application/json" },
    body: JSON.stringify({ photos_pending: waitingPhotoCount(taskId) }),
    taskId,
    countFor: taskId,
    status: "queued",
    attempts: 0,
    nextAt: 0,
  };
  items = [...items, item];
  await saveItem(item);
}

/** With signal: tell the server how many photos are still on the phone. Failing here never matters. */
async function pushCountNow(taskId: string, token: string | null, from: OutboxItem) {
  const apikey = from.headers.apikey;
  let origin: string | null = null;
  try {
    origin = new URL(from.url).origin;
  } catch {
    origin = null;
  }
  if (!apikey || !origin) return;
  try {
    await fetchWithTimeout(
      `${origin}/rest/v1/cleaning_tasks?id=eq.${taskId}`,
      {
        method: "PATCH",
        headers: { apikey, "content-type": "application/json", ...(token ? { authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ photos_pending: waitingPhotoCount(taskId) }),
      },
      REQUEST_TIMEOUT_MS,
    );
  } catch {
    /* the next photo that sends tries again */
  }
}

async function queueAndAnswer(url: string, method: string, init: RequestInit | undefined, target: Target): Promise<Response> {
  const headers = headersToRecord(init?.headers);
  delete headers.authorization;
  const base = { id: newId(), seq: ++seqCounter, createdAt: new Date().toISOString(), method, url, headers, status: "queued" as const, attempts: 0, nextAt: 0 };

  if (target.kind === "photo") {
    let blob: Blob | null = null;
    let cacheControl = "3600";
    const body = init?.body;
    if (body instanceof FormData) {
      const file = body.get("");
      if (file instanceof Blob) blob = file;
      const cc = body.get("cacheControl");
      if (typeof cc === "string") cacheControl = cc;
    } else if (body instanceof Blob) {
      blob = body;
    }
    if (!blob) return jsonResponse(400, { message: "Could not read the photo" });
    const key = `${target.bucket}/${target.path}`;
    const contentType = blob.type || headers["content-type"] || "image/jpeg";
    try {
      await idbPut("photos", { path: key, blob, contentType } satisfies StoredPhoto);
    } catch {
      return jsonResponse(507, { message: "No room to save the photo on this phone" });
    }
    const item: OutboxItem = {
      ...base,
      kind: "photo",
      headers: { ...headers, "x-upsert": headers["x-upsert"] ?? "false" },
      photo: { bucket: target.bucket, path: target.path, contentType, cacheControl, upsert: headers["x-upsert"] === "true" },
    };
    items = [...items, item];
    await saveItem(item);
    const photoTask = taskIdOfPhotoPath(target.path);
    if (photoTask) await queueCountWrite(photoTask, url, headers);
    emit();
    return jsonResponse(200, { Id: base.id, Key: key });
  }

  // table writes and rpc calls
  let bodyText = typeof init?.body === "string" ? init.body : undefined;
  let rowsForAnswer: Record<string, unknown>[] = [];
  if (target.kind === "write" && bodyText) {
    try {
      const parsed = JSON.parse(bodyText) as unknown;
      const list = (Array.isArray(parsed) ? parsed : [parsed]) as Record<string, unknown>[];
      if (method === "POST" && NEEDS_ID.has(target.table)) {
        for (const row of list) if (row && typeof row === "object" && !("id" in row)) row.id = newId();
        bodyText = JSON.stringify(Array.isArray(parsed) ? list : list[0]);
      }
      rowsForAnswer = list.map((r) => ({ ...r }));
      if (method === "PATCH" && target.taskId) rowsForAnswer = rowsForAnswer.map((r) => ({ id: target.taskId, ...r }));
    } catch {
      /* answer below with an empty row */
    }
  }
  const item: OutboxItem = {
    ...base,
    kind: "write",
    body: bodyText,
    taskId: target.kind === "write" ? target.taskId : undefined,
  };
  items = [...items, item];
  await saveItem(item);
  emit();

  if (target.kind === "rpc") return jsonResponse(200, null);
  const prefer = headers.prefer ?? "";
  const wantsRows = prefer.includes("return=representation");
  if (!wantsRows) return new Response(null, { status: method === "POST" ? 201 : 204 });
  const single = (headers.accept ?? "").includes("vnd.pgrst.object");
  return jsonResponse(method === "POST" ? 201 : 200, single ? (rowsForAnswer[0] ?? {}) : rowsForAnswer);
}

function hasWaitingWrites(): boolean {
  return items.some((i) => i.kind === "write" && i.status === "queued");
}

/** Drop-in for `fetch` in the Supabase client. */
export async function offlineAwareFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const { url, method } = describe(input, init);
  const target = targetOf(url, method);
  if (!target) return realFetch(input, init);

  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  // Writes keep their order: while older saves are still waiting, newer ones wait behind them.
  if (offline || (target.kind !== "photo" && hasWaitingWrites())) {
    const answer = await queueAndAnswer(url, method, init, target);
    if (!offline) void flushOutbox();
    return answer;
  }
  try {
    return await fetchWithTimeout(input, init, target.kind === "photo" ? PHOTO_TIMEOUT_MS : REQUEST_TIMEOUT_MS);
  } catch (err) {
    if (isNetworkFailure(err)) return queueAndAnswer(url, method, init, target);
    throw err;
  }
}

// ---------- sending the saved requests ----------

let tokenProvider: (() => Promise<string | null>) | null = null;
export function setOutboxTokenProvider(fn: () => Promise<string | null>) {
  tokenProvider = fn;
}

let flushing = false;
const backoff = (attempts: number) => Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** Math.max(0, attempts - 1));

async function sendOne(item: OutboxItem, token: string | null): Promise<"sent" | "retry" | "failed"> {
  const headers: Record<string, string> = { ...item.headers };
  if (token) headers.authorization = `Bearer ${token}`;
  try {
    let res: Response;
    if (item.kind === "photo" && item.photo) {
      const key = `${item.photo.bucket}/${item.photo.path}`;
      const stored = await idbGet<StoredPhoto>("photos", key);
      if (!stored) return "sent"; // nothing left to send
      const form = new FormData();
      form.append("cacheControl", item.photo.cacheControl);
      form.append("", stored.blob);
      delete headers["content-type"];
      res = await fetchWithTimeout(item.url, { method: item.method, headers, body: form }, PHOTO_TIMEOUT_MS);
    } else {
      res = await fetchWithTimeout(item.url, { method: item.method, headers, body: item.body }, REQUEST_TIMEOUT_MS);
    }
    if (res.ok) return "sent";
    // Already there from an earlier try that got through: nothing more to do.
    if (res.status === 409) return "sent";
    if (res.status === 401 || res.status === 408 || res.status === 425 || res.status === 429 || res.status >= 500) return "retry";
    let reason = `${res.status}`;
    try {
      const j = (await res.json()) as { message?: string; error?: string };
      reason = j.message || j.error || reason;
    } catch {
      /* keep status */
    }
    item.failReason = reason;
    return "failed";
  } catch (err) {
    if (isNetworkFailure(err)) return "retry";
    item.failReason = err instanceof Error ? err.message : "Could not send";
    return "failed";
  }
}

/** Send what is waiting: answers first (tiny), then photos. Stops quietly when there is still no signal. */
export async function flushOutbox(): Promise<void> {
  if (flushing) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  flushing = true;
  try {
    await loadOutbox();
    const token = tokenProvider ? await tokenProvider().catch(() => null) : null;
    const order = (kind: OutboxItem["kind"]) => items.filter((i) => i.kind === kind && i.status === "queued").sort((a, b) => a.seq - b.seq);
    for (const batch of [order("write"), order("photo")]) {
      for (const item of batch) {
        if (item.nextAt > Date.now()) {
          // keep order: a write that is waiting its turn holds back later writes
          if (item.kind === "write") break;
          continue;
        }
        uploading.add(item.id);
        emit();
        const result = await sendOne(item, token);
        uploading.delete(item.id);
        if (result === "sent" || (result === "failed" && item.countFor)) {
          if (item.kind === "photo" && item.photo && result === "sent") {
            const t0 = taskIdOfPhotoPath(item.photo.path);
            if (t0) {
              const cur = sentPhotos.get(t0) ?? { before: 0, after: 0, other: 0 };
              cur[photoGroupOf(item.photo.path)] += 1;
              sentPhotos.set(t0, cur);
            }
          }
          await removeItem(item);
          if (item.kind === "photo" && item.photo) {
            const t = taskIdOfPhotoPath(item.photo.path);
            if (t) await pushCountNow(t, token, item);
          }
          continue;
        }
        item.attempts += 1;
        if (result === "failed") {
          item.status = "failed";
        } else {
          item.nextAt = Date.now() + backoff(item.attempts);
        }
        await saveItem(item);
        emit();
        if (result === "retry" && item.kind === "write") break;
      }
    }
  } finally {
    flushing = false;
    // Something may have been saved while we were sending.
    const more = items.some((i) => i.status === "queued" && i.nextAt <= Date.now());
    if (more && typeof navigator !== "undefined" && navigator.onLine !== false) setTimeout(() => void flushOutbox(), 800);
  }
}

/** Set a failed item back in line (used by the one button on the can't-send card, in the screens stage). */
export async function retryFailed(): Promise<void> {
  for (const it of items) {
    if (it.status === "failed") {
      it.status = "queued";
      it.nextAt = 0;
      await saveItem(it);
    }
  }
  emit();
  void flushOutbox();
}
